import React, { FC, ReactNode, useEffect, useState } from "react";

import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  MenuItem,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DateTimePicker } from "@mui/x-date-pickers";
import {
  ArrowRightBold as ArrowRightBoldIcon,
  Plus as AddIcon,
} from "mdi-material-ui";

import { Form } from "assets/styleds";
import {
  IOcorrenciaPayload,
  ICategoriaOcorrencia,
  ITipoOcorrencia,
  utils,
  yupOcorrencia,
  EUserType,
  IMatricula,
  ITurma,
  EAcaoLogUser,
  Log,
  IAluno,
  EGrupoAcaoLogUser,
  ELogUserStatus,
} from "@deltasge/marauders-map";
import { useAppSelector } from "store";
import { snack } from "components/GlobalSnackbar";
import { api, history } from "configs";
import { getError } from "utils";
import { useSessionStorage } from "hooks/useSessionStorage";
import { format } from "date-fns";
import { gravaLogUser } from "configs/log";
import { useDialog } from "hooks/useDialog";
import { Alunos } from "../components/Alunos";

export interface IOcorrenciaErrors {
  data?: string;
  idTurma?: string;
  idMateria?: string;
  alunos?: string;
  idCategoriaOcorrencia?: string;
  idTipoOcorrencia?: string;
  idProfessor?: string;
  bimestre?: string;
  restrita?: string;
  historico?: string;
}

const yupOcorrenciaParamsMax = yupOcorrencia.fields.historico.tests.find(
  (f) => f.OPTIONS.name == "max"
)?.OPTIONS.params;
const maxLength =
  yupOcorrenciaParamsMax && !Number.isNaN(yupOcorrenciaParamsMax.max)
    ? Number(yupOcorrenciaParamsMax.max)
    : 8000;

export const CadastroOcorrencia: FC<{
  handleClose: (reload?: boolean) => void;
  item: IOcorrenciaPayload;
  goToList?: boolean;
  matriculas?: IMatricula[];
}> = ({ handleClose, item, goToList = false, matriculas: initMatriculas }) => {
  const {
    configuracoes: {
      etapas,
      configOnline: { configuracoes },
    },
    treeView: { cursos },
    usuario: { usuario },
  } = useAppSelector((state) => state);

  const [categorias, setCategorias] = useSessionStorage<ICategoriaOcorrencia[]>(
    "CategoriasOcorrencia",
    [],
    2
  );
  const [tipos, setTipos] = useSessionStorage<ITipoOcorrencia[]>(
    "TiposOcorrencia",
    [],
    2
  );
  const [payload, setPayload] = useState<IOcorrenciaPayload>({
    ...item,
    gerarNotificacao:
      typeof item.gerarNotificacao === "boolean"
        ? item.gerarNotificacao
        : false,
    idMateria: (() => {
      const turma = utils
        .getTodasAsTurmas({ cursos })
        .find((f) => f.id === item.idTurma);

      let materia =
        (turma?.materias ?? []).find((f) => f.id === item.idMateria) ?? null;

      if (!materia && usuario?.tipoUsuario === EUserType.Professor) {
        materia = turma?.materias?.at(0) ?? null;
      }
      return materia?.id ?? null;
    })(),
  });

  const {
    show: showAlunos,
    RenderDialog: DialogAlunos,
    hide: hideAlunos,
  } = useDialog({
    title: "Escolha o(s) aluno(s)",
    options: {
      maxWidth: "sm",
      scroll: "paper",
    },
  });

  const [errors, setErrors] = useState<IOcorrenciaErrors>({});
  const [saving, setSaving] = useState(false);
  const [matriculas, setMatriculas] = useState(initMatriculas || []);

  useEffect(() => {
    if (categorias.length == 0 || tipos.length == 0) {
      getCategorias();
      getTipos();
    }
    if (matriculas.length == 0) {
      getMatriculas();
    }
  }, []);

  const getCategorias = async () => {
    try {
      const { data } = await api.get<[ICategoriaOcorrencia[]]>(
        "categoriaocorrencia",
        {
          params: {
            select: ["id", "descricao"],
            order: { descricao: "ASC" },
          },
        }
      );
      if (Array.isArray(data) && Array.isArray(data[0])) {
        setCategorias(data[0]);
      }
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const getTipos = async () => {
    try {
      const { data } = await api.get<[ITipoOcorrencia[]]>("tipoocorrencia", {
        params: {
          select: ["id", "nome", "idCategoriaOcorrencia", "textoPadrao"],
          order: { nome: "ASC" },
        },
      });
      if (Array.isArray(data) && Array.isArray(data[0])) {
        setTipos(data[0]);
      }
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const getMatriculas = async () => {
    try {
      const { data } = await api.get<ITurma | undefined>(
        `turma/${payload.idTurma}`,
        {
          cache: {
            ignoreCache: true,
          },
          params: {
            select: [
              "id",
              "matriculas.id",
              "matriculas.nrChamada",
              "matriculas.situacao",
              "matriculas.aluno.nome",
              "matriculas.aluno.nomeusual",
              "matriculas.aluno.uid",
              "matriculas.aluno.codigo",
              "matriculas.aluno.imagemAluno.caminhoArquivo",
            ],
            order: {
              matriculas: { nrChamada: "ASC" },
            },
          },
        }
      );
      if (data && Array.isArray(data.matriculas)) {
        setMatriculas(data.matriculas);
      }
    } catch (error) {
      snack.error(getError(error));
    }
  };

  const handleDate = (data: Date | null) => {
    if (!data) {
      setPayload((p) => ({ ...p, data: new Date() }));
      setErrors((p) => ({
        ...p,
        data: "Você deve selecionar uma data",
      }));
    } else {
      setErrors((p) => ({ ...p, data: undefined }));
      const etapa = utils.getEtapaPorData({
        data,
        cursos,
        etapas,
        idTurma: payload.idTurma,
      });
      if (!etapa) {
        setErrors((p) => ({
          ...p,
          data: "Não foi possível encontrar a etapa do curso desta turma para a data atual",
        }));
        snack.warning(
          "Não foi possível encontrar a etapa do curso desta turma para a data atual"
        );
      } else {
        setPayload((p) => ({
          ...p,
          data: data,
          bimestre: etapa.etapa,
        }));
        setErrors((p) => ({ ...p, data: undefined }));
      }
    }
  };

  const handleSubmit = async () => {
    try {
      if (!payload.data) {
        setErrors({
          data: "Você deve selecionar uma data",
        });
        return;
      }
      if (payload.idTiposOcorrencia.length == 0) {
        setErrors({
          idTipoOcorrencia: "Você deve ao menos selecionar um tipo",
        });
        return;
      }
      if (payload.alunos.length == 0) {
        setErrors({
          alunos: "Você deve ao menos selecionar um aluno",
        });
        return;
      }
      setSaving(true);

      if (payload.id) {
        const { id, idTiposOcorrencia, alunos, data, ...rest } = payload;
        await api.put(`ocorrencia/${id}`, {
          ...rest,
          idTipoOcorrencia: idTiposOcorrencia[0],
          idAluno: alunos[0].id,
          data: format(data, "yyyy-MM-dd"),
          hora: format(data, "HH:mm"),
        });

        gravaLogUser({
          logKey: {
            ...Log.Alunos.Alterar,
            acao: EAcaoLogUser.Alteracao,
            grupoAcao: EGrupoAcaoLogUser.Ocorrencia,
          },
          logKeys: {
            idProfessor: Number(payload.idProfessor),
            idMateria: Number(payload.idMateria),
            idTurma: payload.idTurma,
            etapa: Number(payload.bimestre),
          },
          status: ELogUserStatus.Aguardando,
          message: `alterou ocorrência id ${payload.id}`,
          dataBefore: { ...data },
          dataAfter: { ...payload },
        });
      } else {
        await api.post("ocorrencia/payload", payload);

        gravaLogUser({
          logKey: {
            ...Log.Alunos.Incluir,
            acao: EAcaoLogUser.Inclusao,
            grupoAcao: EGrupoAcaoLogUser.Ocorrencia,
          },
          logKeys: {
            idMateria: Number(payload.idMateria),
            idProfessor: Number(payload.idProfessor),
            idTurma: payload.idTurma,
            etapa: Number(payload.bimestre),
          },
          status: ELogUserStatus.Aguardando,
          message: `incluiu ocorrencia na materia id ${payload.idMateria}`,
          dataAfter: { ...payload },
        });
      }
      setSaving(false);
      const s = payload.alunos.length > 1 ? "s" : "";

      let msg: string | ReactNode = `Ocorrência${s} criada${s} com sucesso!`;
      if (goToList) {
        let url = `/ocorrencias/${payload.idTurma}`;
        if (payload.alunos.length == 1) {
          url += `/?idAluno=${payload.alunos[0].id}`;
        }
        msg = (
          <Grid container alignItems="center">
            <Grid item>{msg}</Grid>
            <Grid item>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => {
                  history.push(url);
                }}
              >
                <ArrowRightBoldIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        );
      }
      snack.success(msg);
      handleClose(true);
    } catch (error) {
      setSaving(false);
      snack.error(getError(error));
    }
  };

  const handleCloseAlunos = (alunos?: IAluno[]) => {
    hideAlunos();
    if (alunos && alunos.length > 0) {
      setPayload((p) => ({
        ...p,
        alunos: alunos.map((m) => ({
          id: m?.uid || 0,
          nome: m?.nome || "",
        })),
      }));
    }
  };

  const handleDelete = (aluno: IAluno) => {
    setPayload((p) => ({
      ...p,
      alunos: p.alunos.filter((f) => f.id != aluno.uid),
    }));
  };

  const turma = utils
    .getTodasAsTurmas({ cursos })
    .find((f) => f.id == payload.idTurma);

  let materia =
    (turma?.materias ?? []).find((f) => f.id == payload.idMateria) ?? null;
  if (!materia && usuario?.tipoUsuario == EUserType.Professor) {
    materia = turma?.materias?.at(0) ?? null;
  }

  const categoria = categorias.find(
    (f) => f.id == payload.idCategoriaOcorrencia
  );
  const tiposSelecionados = tipos.filter((f) =>
    payload.idTiposOcorrencia.includes(f.id ?? 0)
  );
  const tiposFiltrados = tipos.filter(
    (f) =>
      f.idCategoriaOcorrencia == payload.idCategoriaOcorrencia ||
      payload.idCategoriaOcorrencia == undefined
  );

  const matriculasSelecionadas = matriculas.filter(
    (f) =>
      f.aluno &&
      f.aluno.uid &&
      payload.alunos.map((m) => m.id).includes(f.aluno.uid)
  );

  const isEdit = item.id != undefined && item.id != null;

  return (
    <>
      <DialogAlunos>
        <Alunos
          handleClose={handleCloseAlunos}
          matriculas={matriculas}
          selecionados={matriculasSelecionadas}
          labelBtn="Adicionar"
        />
      </DialogAlunos>
      <DialogContent dividers style={{ paddingBottom: 0 }}>
        <Form noValidate id="formCadastroOcorrencia">
          <Grid container spacing={2}>
            <Grid item flex={1}>
              <FormLabel sx={{ ml: 1, fontSize: "12px" }}>Alunos</FormLabel>
              <Grid container>
                {matriculasSelecionadas.map((mat, index) => (
                  <Grid key={`aluno-${index}`}>
                    <Chip
                      sx={{ mr: 1, mb: 1 }}
                      label={mat.aluno?.nomeusual || mat.aluno?.nome}
                      onDelete={
                        !isEdit
                          ? () => {
                              if (mat.aluno) handleDelete(mat.aluno);
                            }
                          : undefined
                      }
                    />
                  </Grid>
                ))}
                {matriculasSelecionadas.length == 0 && (
                  <Grid item>
                    <Alert severity="error">Nenhum aluno selecionado</Alert>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item>
              <Button
                size="small"
                endIcon={<AddIcon />}
                onClick={showAlunos}
                disabled={isEdit}
              >
                Adicionar
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <DateTimePicker
                    label="Data e hora"
                    onChange={handleDate}
                    value={payload.data}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={!!errors.data}
                        helperText={errors.data}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Matéria"
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    value={payload.idMateria ?? ""}
                    onChange={({ target: { value } }) =>
                      setPayload((p) => ({
                        ...p,
                        idMateria: value != "" ? parseInt(value, 10) : null,
                      }))
                    }
                  >
                    {usuario?.tipoUsuario != EUserType.Professor && (
                      <MenuItem value="">
                        <em>INDEPENDENTE DA MATÉRIA</em>
                      </MenuItem>
                    )}
                    {(turma?.materias ?? []).map((option) => (
                      <MenuItem key={`materia-${option.id}`} value={option.id}>
                        {option.nome?.rUpper()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    disabled={isEdit}
                    select
                    label="Categoria"
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    value={categoria?.id ?? ""}
                    onChange={({ target: { value } }) => {
                      setPayload((p) => ({
                        ...p,
                        idCategoriaOcorrencia:
                          value != "" ? parseInt(value, 10) : null,
                        idTiposOcorrencia: [],
                      }));
                    }}
                  >
                    <MenuItem value="">
                      <em>TODAS AS CATEGORIAS</em>
                    </MenuItem>
                    {(categorias ?? []).map((option) => (
                      <MenuItem
                        key={`categoria-${option.id}`}
                        value={option.id}
                      >
                        {option.descricao?.rUpper()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    disabled={isEdit}
                    noOptionsText="Nenhum tipo encontrado"
                    sx={{ mb: 2 }}
                    multiple={!isEdit}
                    value={
                      !isEdit
                        ? tiposSelecionados
                        : tiposSelecionados.at(0) || null
                    }
                    options={tiposFiltrados}
                    getOptionLabel={(o) => o.nome ?? ""}
                    onChange={(e, value) => {
                      if (Array.isArray(value)) {
                        setPayload((p) => ({
                          ...p,
                          idTiposOcorrencia: value.map((m) => m.id ?? 0),
                          historico: value.at(0)?.textoPadrao ?? "",
                        }));
                      } else {
                        setPayload((p) => ({
                          ...p,
                          idTiposOcorrencia: value ? [value.id ?? 0] : [],
                          historico: value ? value.textoPadrao : "",
                        }));
                      }
                      setErrors((p) => ({
                        ...p,
                        idTipoOcorrencia: undefined,
                      }));
                    }}
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        label="Tipo"
                        error={!!errors.idTipoOcorrencia}
                        helperText={errors.idTipoOcorrencia}
                      />
                    )}
                    renderOption={(p, o) => (
                      <ListItem {...p}>
                        <ListItemText primary={o.nome} />
                      </ListItem>
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormGroup sx={{ mt: 0.5, mb: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          onChange={({ target: { checked } }) =>
                            setPayload((p) => ({ ...p, restrita: checked }))
                          }
                          checked={payload.restrita}
                        />
                      }
                      label="Não Imprimir no Boletim / Aluno Online"
                    />
                  </FormGroup>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    value={payload.historico}
                    onChange={({ target: { value } }) =>
                      setPayload((p) => ({ ...p, historico: value }))
                    }
                    disabled={configuracoes.bloq_padrao_ocorre}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength }}
                    multiline
                    rows={6}
                    label="Histórico"
                    helperText={`${
                      maxLength - payload.historico.length
                    } caracteres restantes.`}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose()} variant="text" color="inherit">
          Cancelar
        </Button>
        <LoadingButton loading={saving} onClick={handleSubmit}>
          Salvar
        </LoadingButton>
      </DialogActions>
    </>
  );
};
